/**
 * Keeps the theme out of ZipChat's way.
 *
 * Two jobs: stand the theme's search overlay down when ZipChat's search is on
 * screen, and flag the root element while any ZipChat surface covers the page
 * so the sticky add-to-cart bar can hide for it.
 *
 * ZipChat renders its search into an open shadow root on a host it appends to
 * <body>, and pins that host at `display: contents !important`. A host that
 * generates no box opens no stacking context, so the overlay inside it
 * competes for z-index directly with the page — at `search_z_index`, 999 by
 * default. theme.liquid pins `.hhs-search__overlay` at 1000 to clear another
 * app's floating bubble, which lands the theme's own search on top of
 * ZipChat's every single time.
 *
 * Shadow encapsulation means we cannot raise theirs from here, and the host's
 * inline `!important` cannot be outranked, so the theme steps down instead.
 * Rather than hard-code a number under 999 — a store can set search_z_index to
 * anything — read whatever theirs resolves to and sit one below it.
 *
 * None of this runs unless that shadow host actually appears, so a store
 * without the app, or with its search feature switched off, behaves exactly as
 * it did before.
 */
const HOST_ID = "zipchat-shadow-host";
const OVERLAY_ID = "zipchat-search-overlay";
const CHAT_ID = "zipchat-iframe";
const WIRE_POLL_MS = 250;
const WIRE_TIMEOUT_MS = 30000;

const root = document.documentElement;

function stepAside(overlay) {
  const z = Number.parseInt(getComputedStyle(overlay).zIndex, 10);
  if (Number.isFinite(z)) {
    root.style.setProperty("--hhs-search-z", String(z - 1));
  }
  root.setAttribute("data-zipchat-search", "open");

  // One click can open both. ZipChat stops the event on the trigger it
  // hijacks, but that only silences listeners registered after its own, and
  // nothing orders an app embed script against a theme module.
  const ours = document.querySelector("predictive-search");
  if (ours && ours.isOpen && typeof ours.close === "function") ours.close();
}

function stepBack() {
  root.style.removeProperty("--hhs-search-z");
  root.removeAttribute("data-zipchat-search");
}

function chatIsOpen(shadow) {
  const iframe = shadow.getElementById(CHAT_ID);
  // They open and close the chat window by writing opacity and transform
  // straight onto the element, so the inline style is the state.
  return Boolean(iframe && iframe.style.opacity === "1");
}

function watchShadow(shadow) {
  const check = () => {
    const overlay = shadow.getElementById(OVERLAY_ID);
    if (overlay) stepAside(overlay);
    else stepBack();

    // Anything of theirs that covers the page. The sticky add-to-cart bar
    // sits above their chat bubble and would otherwise stay on top of an
    // open chat window too; it hides on this flag the same way it already
    // hides for the theme's own drawers.
    root.toggleAttribute(
      "data-zipchat-open",
      Boolean(overlay) || chatIsOpen(shadow),
    );
  };
  new MutationObserver(check).observe(shadow, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style"],
  });
  check();
}

let wired = false;

function tryWire() {
  if (wired) return true;
  const host = document.getElementById(HOST_ID);
  if (!host || !host.shadowRoot) return false;
  wired = true;
  watchShadow(host.shadowRoot);
  return true;
}

if (!tryWire()) {
  const observer = new MutationObserver(() => {
    if (tryWire()) observer.disconnect();
  });
  observer.observe(root, { childList: true, subtree: true });

  // The host is appended before its shadow root is attached, so the mutation
  // announcing it can arrive a tick too early — and the attach itself is not a
  // mutation we are able to observe.
  const poll = setInterval(() => {
    if (tryWire()) {
      clearInterval(poll);
      observer.disconnect();
    }
  }, WIRE_POLL_MS);
  setTimeout(() => clearInterval(poll), WIRE_TIMEOUT_MS);
}
